<template>
  <div
    class="practice-page"
    @touchstart.capture="handleQuestionTouchStart"
    @touchmove.capture="handleQuestionTouchMove"
    @touchend.capture="handleQuestionTouchEnd"
    @touchcancel.capture="resetQuestionTouch"
  >
    <skill-header :title="pageTitle" title-align="left" @back="handleBack">
      <button v-if="showQuestionStatisticsButton" class="history-button" type="button" @click="requestStatistics">
        <img src="@/assets/images/skillCenterQuestion/answer-card.png" alt="统计">
      </button>
    </skill-header>

    <main v-if="currentQuestion" class="practice-content">
      <section class="question-meta">
        <div class="question-tags">
          <span>{{ questionTypeText }}</span>
        </div>
        <div class="question-index"><b>{{ displayQuestionIndex }}</b><span>/{{ displayQuestionTotal }}</span></div>
      </section>

      <section class="question-body">
        <div v-if="currentQuestion.publicContent" class="question-title question-title--public" v-html="currentQuestion.publicContent" />
        <question-media
          v-if="publicMedia"
          :key="`public-${currentIndex}`"
          :src="publicMedia"
          :type="mediaKind(publicMedia)"
        />
        <div
          class="question-title"
          :class="{ 'essay-question-title': Number(currentQuestion.type) === 8 }"
          v-html="currentQuestion.content"
        />
        <question-media
          v-if="questionMedia"
          :key="`question-${currentIndex}`"
          :src="questionMedia"
          :type="mediaKind(questionMedia)"
        />

        <div v-if="currentQuestion.selectList && currentQuestion.selectList.length" class="option-list">
          <button
            v-for="option in currentQuestion.selectList"
            :key="option.id || option.type"
            type="button"
            class="option-item"
            :class="optionClass(option)"
            :disabled="isAnswered || isScoreDetailReview || practiceMode === 'recite' || testModeAdvancing"
            @click="selectOption(option)"
          >
            <span class="option-letter">{{ option.type }}</span>
            <span class="option-divider" />
            <span class="option-text" v-html="option.name" />
            <span v-if="showAnswer && isWrongOption(option)" class="option-wrong" aria-label="错误" />
          </button>
        </div>

        <div v-if="currentQuestion.type == 7" class="fill-list">
          <input
            v-for="(blank, index) in currentQuestion.blanksList"
            :key="index"
            v-model="blank.name"
            :disabled="isAnswered || isScoreDetailReview || practiceMode === 'recite'"
            :placeholder="`${index + 1}.请输入内容`"
            @blur="practiceMode === 'fast' && submitCurrentAnswer()"
          >
        </div>

        <div v-if="currentQuestion.type == 8" class="essay-box">
          <textarea
            v-model="essayAnswer"
            :disabled="isAnswered || isScoreDetailReview || practiceMode === 'recite'"
            placeholder="请输入答案"
            style="-webkit-text-size-adjust: 100%; font-size: 14px; line-height: 24px;"
            @blur="practiceMode === 'fast' && submitCurrentAnswer()"
          />
        </div>

        <section v-if="showAnswer" class="answer-detail">
          <div class="answer-title">{{ practiceMode === 'test' ? '答案批改' : '答案' }}</div>
          <div v-if="currentQuestion.type != 8" class="answer-line">
            正确答案<span class="answer-correct">{{ currentQuestion.correct }}</span><template v-if="practiceMode !== 'recite'">，你的答案<span class="answer-user">{{ displayResult }}</span>，{{ answerText }}</template>。
            <button type="button" @click="openCorrection">纠错</button>
          </div>
          <div v-else class="essay-answer" v-html="currentQuestion.correct" />
          <div v-if="currentQuestion.analysis" class="analysis-title">解析</div>
          <div v-if="currentQuestion.analysis" class="analysis-content" v-html="currentQuestion.analysis" />
        </section>
      </section>
    </main>

    <div v-else-if="!loading" class="empty-state"><span>当前目录暂无题目</span></div>

    <footer v-if="currentQuestion" class="practice-footer">
      <div class="footer-nav">
        <button
          type="button"
          class="footer-nav-button"
          :disabled="currentTypeQuestionPosition <= 0"
          @click="previousQuestion"
        >上一题</button>
        <button type="button" class="footer-nav-button" @click="nextQuestion">下一题</button>
      </div>
      <button
        v-if="practiceMode !== 'test' && practiceMode !== 'fast'"
        type="button"
        class="submit-footer-button"
        :disabled="isAnswered || !canSubmitAnswer"
        @click="submitCurrentAnswer"
      >提交答案</button>
      <button type="button" class="tool-button note-tool" @click="openNote">
        <img src="@/assets/images/skillCenterQuestion/note.png" alt="笔记">
        <span>笔记</span>
      </button>
      <button type="button" class="tool-button favorite-tool" :class="{ active: currentQuestion.isFavorite == 1 }" @click="toggleFavorite">
        <img :src="favoriteIcon" alt="收藏">
        <span>收藏</span>
      </button>
    </footer>

    <transition name="fade">
      <section v-if="answerCardVisible" class="answer-card">
        <skill-header :title="pageTitle" title-align="left" @back="handleAnswerCardBack">
          <button v-if="isPracticeSession" class="history-button" type="button" @click="confirmReset">
            <img src="@/assets/images/skillCenterQuestion/reset.png" alt="重做">
          </button>
        </skill-header>
        <div class="answer-filters">
          <div class="filter-row type-filter-row">
            <span>题型</span>
            <div class="filter-scroll">
              <button v-for="item in typeFilters" :key="item.value" type="button" :class="{ active: cardType === item.value }" @click="cardType = item.value">{{ item.label }}</button>
            </div>
          </div>
          <div class="filter-row">
            <span>分类</span>
            <button v-for="item in categoryFilters" :key="item.value" type="button" :class="{ active: cardCategory === item.value }" @click="cardCategory = item.value">{{ item.label }}</button>
          </div>
          <div class="filter-row">
            <span>模式</span>
            <button v-for="item in modeFilters" :key="item.value" type="button" :class="{ active: practiceMode === item.value }" @click="changePracticeMode(item.value)">{{ item.label }}</button>
          </div>
        </div>
        <div class="answer-grid">
          <button
            v-for="item in cardQuestions"
            :key="item.question.id"
            type="button"
            :class="answerNumberClass(item.question)"
            @click="jumpToQuestion(item.index)"
          >{{ item.index + 1 }}</button>
        </div>
        <div v-if="isPracticeSession" class="answer-summary">当前{{ questions.length }}题，对{{ rightCount }}题，错{{ wrongCount }}题，未做{{ unansweredCount }}题，正确率 <b>{{ accuracy }}%</b></div>
      </section>
    </transition>

    <van-popup v-model="noteVisible" position="bottom" round class="bottom-sheet note-sheet" :style="noteSheetStyle" :close-on-click-overlay="true">
      <div class="sheet-handle" />
      <textarea v-model="noteContent" placeholder="请输入笔记内容" @focus="handleNoteFocus" @blur="handleNoteBlur" />
      <div class="sheet-actions">
        <div class="uploaded-images">
          <span v-for="(url, index) in noteImages" :key="url">
            <img :src="resolveMedia(url)" alt="笔记图片">
            <button type="button" @click="noteImages.splice(index, 1)">−</button>
          </span>
        </div>
        <button v-if="isAndroid" type="button" class="photo-button" @click="openPhotoPicker('note')"><img class="picture-icon" src="@/assets/images/skillCenterQuestion/picture.png" alt="图片"></button>
        <van-uploader v-else :after-read="uploadNoteImage" :max-count="1"><img class="picture-icon" src="@/assets/images/skillCenterQuestion/picture.png" alt="图片"></van-uploader>
        <button type="button" :disabled="!noteContent.trim() && !noteImages.length" @click="saveNote">保存</button>
      </div>
    </van-popup>

    <van-popup v-model="correctionVisible" position="bottom" round class="bottom-sheet correction-sheet" :style="correctionSheetStyle" :close-on-click-overlay="true">
      <div class="sheet-handle" />
      <div class="correction-types">
        <button v-for="item in correctionTypes" :key="item.value" type="button" :class="{ active: correctionType === item.value }" @click="correctionType = item.value">{{ item.label }}</button>
      </div>
      <textarea v-model="correctionContent" placeholder="优质纠错内容被采纳，我们会联系你进行奖励。" @focus="handleCorrectionFocus" @blur="handleCorrectionBlur" />
      <div class="sheet-actions">
        <div class="uploaded-images">
          <span v-for="(url, index) in correctionImages" :key="url">
            <img :src="resolveMedia(url)" alt="纠错图片">
            <button type="button" @click="correctionImages.splice(index, 1)">−</button>
          </span>
        </div>
        <button v-if="isAndroid" type="button" class="photo-button" @click="openPhotoPicker('correction')"><img class="picture-icon" src="@/assets/images/skillCenterQuestion/picture.png" alt="图片"></button>
        <van-uploader v-else :after-read="uploadCorrectionImage" :max-count="3"><img class="picture-icon" src="@/assets/images/skillCenterQuestion/picture.png" alt="图片"></van-uploader>
        <button type="button" :disabled="!correctionContent.trim()" @click="submitCorrection">发布</button>
      </div>
    </van-popup>

    <van-overlay :show="resetVisible" class="dialog-overlay">
      <section class="skill-dialog reset-dialog" @click.stop>
        <h3>温馨提示</h3>
        <p>重做仅清空答案，重做时，错题将移除<br>是否重做本章节题目？</p>
        <button type="button" class="dialog-primary" @click="resetAnswers">重做本章节所有题目</button>
        <button type="button" class="dialog-secondary" @click="resetVisible = false">取消</button>
      </section>
    </van-overlay>

    <van-overlay :show="isPracticeSession && exitVisible" class="dialog-overlay">
      <section class="skill-dialog exit-dialog" @click.stop>
        <h3>温馨提示</h3>
        <p>还有{{ unansweredCount }}题未完成，确定要统计吗？</p>
        <div class="dialog-inline-actions">
          <button type="button" @click="closeExitDialog">取消</button>
          <button type="button" @click="confirmExit">确定</button>
        </div>
      </section>
    </van-overlay>

    <van-overlay :show="isPracticeSession && statsVisible" class="stats-overlay" @click="closeStatistics">
      <div class="stats-card" @click.stop>
        <h3 v-if="showStatisticsChapter" class="stats-chapter-name">{{ statisticsChapterName }}</h3>
        <p v-if="showStatisticsChapter" class="stats-title">{{ pageTitle }}</p>
        <h3 v-else class="stats-title stats-title--solo">{{ pageTitle }}</h3>
        <div class="stats-gauge" :style="gaugeStyle"><div><b>{{ accuracy }}</b><span>%</span><small>正确率</small></div></div>
        <div class="stats-values">
          <div><b>{{ questions.length }}</b><span>总题数</span></div>
          <div><b>{{ rightCount + wrongCount }}</b><span>答题数</span></div>
          <div><b>{{ rightCount }}</b><span>答对数</span></div>
          <div><b>{{ wrongCount }}</b><span>答错数</span></div>
        </div>
      </div>
      <button type="button" class="continue-button" @click.stop="closeStatistics">继续答题</button>
    </van-overlay>
  </div>
</template>

<script>
import {
  addQuestion,
  addQuestionResource,
  addQuestionResult,
  combinationAnswerSheet,
  getQuestionsList,
  getSkillCenterQuestionPage,
  getSkillCenterResourceQuestionPage,
  getSkillCenterWrongQuestionPage,
  getSkillHomeworkAnswers,
  saveSkillHomeworkAnswer,
  getWrongAnswerQuestions,
  questionFeedBackErrorAdd,
  resetQuestionRecords,
  resetSkillHomeworkContent,
  studyPaperGetById,
  studyRecordRenew
} from '@/api/subject/index.js'
import { uploadImg } from '@/api/system/config'
import { closeSkillCenterWeb } from '@/utils/skillCenter'
import { isAuthError, waitForAppToken } from '@/utils/appToken'
import { Toast } from 'vant'
import favoriteActiveIcon from '@/assets/images/skillCenterQuestion/favorite-active.png'
import favoriteIcon from '@/assets/images/skillCenterQuestion/favorite.png'
import QuestionMedia from './QuestionMedia.vue'
import SkillHeader from './SkillHeader.vue'
import {
  QUESTION_TYPES,
  isAnswerRight,
  mediaUrl,
  normalizeQuestions,
  parseAnswer,
  parseQueryArray,
  questionTypeLabel,
  readSkillCenterGroupRecords,
  writeSkillCenterGroupRecords
} from './shared'

export default {
  name: 'SkillCenterQuestionPractice',
  components: { QuestionMedia, SkillHeader },
  data() {
    return {
      loading: true,
      questions: [],
      records: [],
      currentIndex: 0,
      answerCardVisible: false,
      cardType: 'all',
      cardCategory: 'all',
      practiceMode: 'practice',
      noteVisible: false,
      noteContent: '',
      noteImages: [],
      noteKeyboardOffset: 0,
      noteKeyboardFocused: false,
      correctionKeyboardFocused: false,
      keyboardViewportHeight: 0,
      noteKeyboardTimers: [],
      noteKeyboardViewportHandler: null,
      noteKeyboardWindowHandler: null,
      authExpiredHandler: null,
      authExitTimer: null,
      correctionVisible: false,
      correctionType: '0',
      correctionContent: '',
      correctionImages: [],
      resetVisible: false,
      exitVisible: false,
      statsVisible: false,
      essayAnswer: '',
      touchStartX: 0,
      touchStartY: 0,
      touchStartTarget: null,
      testModeAdvanceTimer: null,
      testModeAdvancing: false,
      isAndroid: /Android|Adr/i.test(navigator.userAgent),
      typeFilters: [{ value: 'all', label: '全部' }],
      categoryFilters: [
        { value: 'all', label: '全部' },
        { value: 'wrong', label: '做错的' },
        { value: 'favorite', label: '收藏的' },
        { value: 'unanswered', label: '未做的' }
      ],
      modeFilters: [
        { value: 'practice', label: '练习模式' },
        { value: 'fast', label: '快刷模式' },
        { value: 'test', label: '测试模式' },
        { value: 'recite', label: '背题模式' }
      ],
      correctionTypes: [
        { value: '0', label: '题目有误' },
        { value: '1', label: '答案有误' },
        { value: '2', label: '解析有误' },
        { value: '3', label: '其他问题' }
      ]
    }
  },
  computed: {
    source() {
      return this.$route.query.source || this.$route.query.name || 'topic'
    },
    majorType() {
      return this.$route.query.majorType || ''
    },
    categoryId() {
      return String(this.$route.query.categoryId || '')
    },
    categoryIds() {
      return parseQueryArray(this.$route.query.categoryIds)
    },
    organCategoryId() {
      return String(this.$route.query.organCategoryId || '')
    },
    isSkillCenterResourcePage() {
      return String(this.$route.query.skillCenter) === '1' &&
        ['wrong', 'errorCorrection', 'collect', 'collection', 'note'].includes(this.source) &&
        Boolean(this.organCategoryId)
    },
    isSkillCenterGroupPage() {
      return String(this.$route.query.skillCenter) === '1' && this.source === 'group'
    },
    isPracticeSession() {
      return !['wrong', 'errorCorrection', 'collect', 'collection', 'note'].includes(this.source)
    },
    isHomeworkMode() {
      return String(this.$route.query.homeworkId || '') !== '' &&
        String(this.$route.query.homeworkContentId || '') !== ''
    },
    isScoreDetailReview() {
      return this.isHomeworkMode && String(this.$route.query.scoreDetail) === '1'
    },
    showQuestionStatisticsButton() {
      return this.isPracticeSession && !['group', 'groupQuestion', 'testCreation'].includes(this.source)
    },
    hasAnswerCardFlow() {
      return this.isPracticeSession || ['wrong', 'collect', 'collection', 'note'].includes(this.source)
    },
    currentQuestion() {
      return this.questions[this.currentIndex] || null
    },
    currentRecord() {
      return this.records[this.currentIndex] || {}
    },
    pageTitle() {
      return this.$route.query.label || '技能刷题'
    },
    statisticsChapterName() {
      return this.$route.query.chapterName ||
        this.$route.query.contextLabel ||
        this.$route.query.detailLabel ||
        (this.currentQuestion && (
          this.currentQuestion.chapterName ||
          this.currentQuestion.contextLabel ||
          this.currentQuestion.categoryName
        )) ||
        ''
    },
    showStatisticsChapter() {
      return Boolean(this.statisticsChapterName && this.statisticsChapterName !== this.pageTitle)
    },
    questionTypeText() {
      return questionTypeLabel(this.currentQuestion && this.currentQuestion.type)
    },
    isMultiple() {
      return Boolean(this.currentQuestion && Number(this.currentQuestion.type) === 5)
    },
    isAnswered() {
      return Boolean(this.currentQuestion && this.currentQuestion.result)
    },
    showAnswer() {
      return this.practiceMode === 'recite' || (this.isAnswered && this.practiceMode !== 'test')
    },
    canSubmitAnswer() {
      if (!this.currentQuestion || this.isAnswered || this.isScoreDetailReview || this.practiceMode === 'recite') return false
      const type = Number(this.currentQuestion.type)
      if (type === 5) return Boolean((this.currentQuestion.selectedList || []).length)
      if (type === 7) {
        const blanks = this.currentQuestion.blanksList || []
        return Boolean(blanks.length && blanks.every((item) => String(item.name || '').trim()))
      }
      if (type === 8) return Boolean(String(this.essayAnswer || '').trim())
      return Boolean(this.currentQuestion.pendingResult)
    },
    displayResult() {
      return this.currentQuestion && this.currentQuestion.result
        ? this.currentQuestion.result
        : '未作答'
    },
    answerText() {
      return this.currentQuestion && isAnswerRight(this.currentQuestion, this.currentQuestion.result)
        ? '回答正确'
        : '回答错误'
    },
    publicMedia() {
      return this.resolveMedia(this.currentQuestion && this.currentQuestion.publicFileUrl)
    },
    questionMedia() {
      return this.resolveMedia(this.currentQuestion && this.currentQuestion.fileUrl)
    },
    cardQuestions() {
      return this.questions
        .map((question, index) => ({ question, index }))
        .filter(({ question }) => {
          if (this.cardType !== 'all') {
            const typeValues = String(this.cardType).split(',')
            if (!typeValues.includes(String(question.type))) return false
          }
          if (this.cardCategory === 'wrong' && question.isRight !== false) return false
          if (this.cardCategory === 'favorite' && Number(question.isFavorite) !== 1) return false
          if (this.cardCategory === 'unanswered' && question.result) return false
          return true
        })
    },
    typeQuestionSequence() {
      const typeValues = this.cardType === 'all'
        ? null
        : new Set(String(this.cardType).split(',').map((value) => value.trim()).filter(Boolean))
      return this.questions
        .map((question, index) => ({ question, index }))
        .filter(({ question }) => !typeValues || typeValues.has(String(question.type)))
    },
    currentTypeQuestionPosition() {
      const position = this.typeQuestionSequence.findIndex(({ index }) => index === this.currentIndex)
      return position === -1 ? 0 : position
    },
    displayQuestionIndex() {
      return this.typeQuestionSequence.length ? this.currentTypeQuestionPosition + 1 : this.currentIndex + 1
    },
    displayQuestionTotal() {
      return this.typeQuestionSequence.length || this.questions.length
    },
    selectedQuestionTypeLabel() {
      if (this.cardType === 'all') return ''
      return String(this.cardType)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => {
          const filter = this.typeFilters.find((item) => String(item.value) === value)
          return filter ? filter.label : questionTypeLabel(value)
        })
        .join('、')
    },
    rightCount() {
      return this.questions.filter((question) => question.result && isAnswerRight(question, question.result)).length
    },
    wrongCount() {
      return this.questions.filter((question) => question.result && !isAnswerRight(question, question.result) && Number(question.type) !== 8).length
    },
    unansweredCount() {
      return this.questions.filter((question) => !question.result).length
    },
    accuracy() {
      const answered = this.rightCount + this.wrongCount
      return answered ? ((this.rightCount / answered) * 100).toFixed(1) : '0.0'
    },
    gaugeStyle() {
      const degree = Number(this.accuracy) * 1.8
      return {
        background: `conic-gradient(from 270deg, #2468ff 0deg ${degree}deg, #f0f1f4 ${degree}deg 180deg, transparent 180deg)`
      }
    },
    favoriteIcon() {
      return Number(this.currentQuestion && this.currentQuestion.isFavorite) === 1
        ? favoriteActiveIcon
        : favoriteIcon
    },
    noteSheetStyle() {
      return this.noteKeyboardOffset
        ? { bottom: `${this.noteKeyboardOffset}px` }
        : {}
    },
    correctionSheetStyle() {
      return this.noteKeyboardOffset
        ? { bottom: `${this.noteKeyboardOffset}px` }
        : {}
    }
  },
  watch: {
    currentIndex() {
      this.clearTestModeAdvance()
      this.syncCurrentQuestion()
    },
    practiceMode(value) {
      if (value === 'recite') this.cardCategory = 'all'
      if (value !== 'test') this.clearTestModeAdvance()
    },
    noteVisible(value) {
      if (value) {
        this.noteKeyboardOffset = 0
        this.noteKeyboardFocused = false
        this.keyboardViewportHeight = this.getKeyboardViewportHeight()
        this.scheduleNoteKeyboardSync()
      } else {
        this.noteKeyboardFocused = false
        this.resetKeyboardSheetPositionIfClosed()
      }
    },
    correctionVisible(value) {
      if (value) {
        this.noteKeyboardOffset = 0
        this.correctionKeyboardFocused = false
        this.keyboardViewportHeight = this.getKeyboardViewportHeight()
        this.scheduleNoteKeyboardSync()
      } else {
        this.correctionKeyboardFocused = false
        this.resetKeyboardSheetPositionIfClosed()
      }
    }
  },
  mounted() {
    this.bindNoteKeyboardListeners()
    this.authExpiredHandler = () => this.handleAuthFailure()
    window.addEventListener('skillCenterAuthExpired', this.authExpiredHandler)
  },
  beforeDestroy() {
    this.unbindNoteKeyboardListeners()
    this.clearNoteKeyboardTimers()
    if (this.authExpiredHandler) {
      window.removeEventListener('skillCenterAuthExpired', this.authExpiredHandler)
    }
    if (this.authExitTimer) clearTimeout(this.authExitTimer)
    this.clearTestModeAdvance()
  },
  async created() {
    if (String(this.$route.query.preview) !== '1') {
      try {
        await waitForAppToken()
      } catch (error) {
        this.handleAuthFailure()
        return
      }
    }
    if (this.$route.query.practiceMode) {
      this.changePracticeMode(this.$route.query.practiceMode, false)
    }
    await this.loadQuestions()
    this.answerCardVisible = this.hasAnswerCardFlow && String(this.$route.query.preview) !== '1' && this.questions.length > 0
    this.applyFocusIndexState()
    this.applyPreviewState()
  },
  methods: {
    normalizePracticeMode(value) {
      const legacyModes = { quick: 'fast', review: 'recite' }
      return legacyModes[value] || value
    },
    changePracticeMode(value, syncRoute = true) {
      const normalizedValue = this.normalizePracticeMode(value)
      if (!this.modeFilters.some((item) => item.value === normalizedValue)) return
      this.practiceMode = normalizedValue
      if (!syncRoute || String(this.$route.query.practiceMode || '') === normalizedValue) return
      this.$router.replace({
        query: {
          ...this.$route.query,
          practiceMode: normalizedValue
        }
      }).catch(() => {})
    },
    handleAuthFailure() {
      if (this.authExitTimer) return
      this.loading = false
      Toast('登录状态已失效，请重新进入')
      this.authExitTimer = setTimeout(() => {
        closeSkillCenterWeb(this, 'skillCenterQuestion')
      }, 1200)
    },
    async loadQuestions() {
      this.loading = true
      const toast = Toast.loading({
        message: this.isSkillCenterGroupPage ? '系统正在进行匹配，请耐心等待' : '加载中',
        duration: 0,
        forbidClick: true
      })
      try {
        if (String(this.$route.query.preview) === '1') {
          this.loadPreviewQuestions()
        } else if (this.source === 'groupQuestion' || this.source === 'testCreation') {
          await this.loadPaperQuestions()
        } else if (this.source === 'wrong' || this.source === 'errorCorrection') {
          if (this.isSkillCenterResourcePage) {
            await this.loadSkillCenterResourcePage('wrong')
          } else {
            const categoryIds = parseQueryArray(this.$route.query.categoryIds)
            const res = await getWrongAnswerQuestions({ categoryIds, majorType: this.majorType })
            this.questions = normalizeQuestions((res && res.data) || [], (res && res.data) || [])
          }
        } else if (['collect', 'collection', 'note'].includes(this.source)) {
          if (this.isSkillCenterResourcePage) {
            await this.loadSkillCenterResourcePage(this.source === 'note' ? 'note' : 'favorite')
          } else {
            const categoryIds = parseQueryArray(this.$route.query.categoryIds)
            const res = await getQuestionsList({
              categoryIds,
              type: this.source === 'note' ? 1 : 0,
              majorType: this.majorType
            })
            this.questions = normalizeQuestions((res && res.data) || [], (res && res.data) || [])
          }
        } else {
          await this.loadNewQuestionPage()
        }
        this.records = this.questions.map((question) => ({
          questionId: question.id,
          result: question.result || '',
          isRight: question.isRight
        }))
        this.currentIndex = 0
        this.buildTypeFilters()
        this.syncCurrentQuestion()
      } catch (error) {
        if (isAuthError(error)) {
          setTimeout(() => this.handleAuthFailure(), 0)
          return
        }
        Toast(error && error.msg ? error.msg : '题目加载失败')
      } finally {
        toast.clear()
        this.loading = false
      }
    },
    async loadNewQuestionPage() {
      if (!this.categoryId && !this.categoryIds.length) {
        Toast('未获取到题库目录')
        return
      }
      const limit = Math.min(Number(this.$route.query.questionLimit) || 1000, 1000)
      const groupRecord = this.isSkillCenterGroupPage ? this.getGroupRecord() : null
      const params = {
        pageNum: 1,
        pageSize: this.isSkillCenterGroupPage ? 1000 : limit
      }
      if (this.isSkillCenterGroupPage && this.categoryIds.length) {
        params.categoryIds = this.categoryIds.join(',')
      } else if (this.categoryIds.length > 1) {
        params.categoryIds = this.categoryIds.join(',')
      } else {
        params.categoryId = this.categoryId || this.categoryIds[0]
      }
      const questionRes = await getSkillCenterQuestionPage(params)
      const rows = (questionRes && questionRes.data && questionRes.data.rows) || []
      const questionRows = this.isHomeworkMode
        ? this.clearHomeworkQuestionHistory(rows)
        : rows
      const questions = normalizeQuestions(questionRows, this.isHomeworkMode ? [] : rows)
      if (this.isHomeworkMode) {
        questions.forEach((question) => {
          this.$set(question, 'result', '')
          this.$set(question, 'isRight', null)
          this.$set(question, 'pendingResult', '')
          if (Array.isArray(question.selectedList)) this.$set(question, 'selectedList', [])
          if (Array.isArray(question.selectList)) {
            question.selectList.forEach((option) => this.$set(option, 'selected', false))
          }
          if (Array.isArray(question.blanksList)) {
            question.blanksList.forEach((blank) => this.$set(blank, 'name', ''))
          }
        })
        const answerRes = await getSkillHomeworkAnswers(String(this.$route.query.homeworkContentId))
        const answerRows = (answerRes && answerRes.data) || []
        const answerMap = new Map()
        answerRows.forEach((answer) => {
          if (answer && answer.questionId !== undefined && answer.questionId !== null) {
            answerMap.set(String(answer.questionId), answer)
          }
        })
        questions.forEach((question) => {
          const answer = answerMap.get(String(question.id))
          if (!answer) return
          const result = answer.result || ''
          const selectedList = parseAnswer(result)
          this.$set(question, 'result', result)
          this.$set(question, 'isRight', answer.isCorrect === 1)
          if (Number(question.type) === 5) {
            this.$set(question, 'selectedList', selectedList)
          }
          if (Array.isArray(question.selectList)) {
            question.selectList.forEach((option) => {
              this.$set(option, 'selected', selectedList.includes(String(option.type)))
            })
          }
          if (Number(question.type) === 7 && Array.isArray(question.blanksList)) {
            const resultList = result ? String(result).split('[$]') : []
            question.blanksList.forEach((blank, index) => {
              this.$set(blank, 'name', resultList[index] || '')
            })
          }
        })
      }
      this.questions = this.isSkillCenterGroupPage
        ? this.prepareGroupQuestions(this.filterGroupQuestions(questions, groupRecord), limit, groupRecord)
        : questions
    },
    clearHomeworkQuestionHistory(rows) {
      return (rows || []).map((question) => {
        const cleared = {
          ...question,
          result: '',
          isRight: null,
          isCorrect: null,
          pendingResult: '',
          selectedList: []
        }
        if (Array.isArray(question.selectList)) {
          cleared.selectList = question.selectList.map((option) => ({
            ...option,
            selected: false
          }))
        }
        if (Array.isArray(question.blanksList)) {
          cleared.blanksList = question.blanksList.map((blank) => ({
            ...blank,
            name: ''
          }))
        }
        if (Array.isArray(question.childedList)) {
          cleared.childedList = this.clearHomeworkQuestionHistory(question.childedList)
        }
        return cleared
      })
    },
    filterGroupQuestions(questions, groupRecord) {
      const selectedTypes = parseQueryArray(this.$route.query.questionTypes || (groupRecord && groupRecord.questionTypes))
      let selectedCategories = parseQueryArray(this.$route.query.groupCategories || (groupRecord && groupRecord.categories))
      if (!selectedCategories.length && groupRecord && groupRecord.classification) {
        const legacyValues = { '1': 'undone', '2': 'wrong', '3': 'favorite' }
        const value = legacyValues[String(groupRecord.classification)] || String(groupRecord.classification)
        selectedCategories = value === 'all' ? ['wrong', 'favorite', 'undone'] : [value]
      }
      const allTypeValues = ['0', '1', '2', '3', '4', '5', '6', '7', '8']
      const allCategoryValues = ['wrong', 'favorite', 'undone']
      const filterByType = selectedTypes.length > 0 && !allTypeValues.every((value) => selectedTypes.includes(value))
      const filterByCategory = selectedCategories.length > 0 && !allCategoryValues.every((value) => selectedCategories.includes(value))
      return questions.filter((question) => {
        if (filterByType && !selectedTypes.includes(String(question.type))) return false
        if (!filterByCategory) return true
        return selectedCategories.some((category) => {
          if (category === 'wrong') return question.isRight === false || String(question.isRight) === '0'
          if (category === 'favorite') return Number(question.isFavorite) === 1
          if (category === 'undone') {
            return question.isRight === undefined || question.isRight === null || question.isRight === ''
          }
          return false
        })
      })
    },
    getGroupRecord() {
      const recordId = String(this.$route.query.groupRecordId || '')
      if (!recordId) return null
      return readSkillCenterGroupRecords().find((record) => {
        return String(record.id || record.studyPaperId || '') === recordId
      }) || null
    },
    saveGroupRecord(record) {
      if (!record) return
      const records = readSkillCenterGroupRecords()
      const index = records.findIndex((item) => {
        return String(item.id || item.studyPaperId || '') === String(record.id || record.studyPaperId || '')
      })
      if (index < 0) return
      records.splice(index, 1, record)
      writeSkillCenterGroupRecords(records)
    },
    prepareGroupQuestions(questions, limit, groupRecord) {
      if (!groupRecord) return questions.slice(0, limit)
      const isNewPaper = String(this.$route.query.groupNew) === '1' && !groupRecord.paperInitialized
      let result
      if (isNewPaper || !Array.isArray(groupRecord.questionIds) || !groupRecord.questionIds.length) {
        result = questions.slice(0, limit)
        groupRecord.questionIds = result.map((question) => String(question.id))
        groupRecord.answers = isNewPaper ? {} : this.createGroupAnswerSnapshot(result)
        groupRecord.paperInitialized = true
      } else {
        const questionMap = new Map(questions.map((question) => [String(question.id), question]))
        result = groupRecord.questionIds.map((id) => questionMap.get(String(id))).filter(Boolean)
      }
      this.applyGroupAnswerState(result, groupRecord.answers || {})
      groupRecord.questionNum = result.length
      groupRecord.answerNum = result.filter((question) => Boolean(question.result)).length
      this.saveGroupRecord(groupRecord)
      return result
    },
    createGroupAnswerSnapshot(questions) {
      return questions.reduce((answers, question) => {
        if (question.result) {
          answers[String(question.id)] = {
            result: question.result,
            isRight: question.isRight
          }
        }
        return answers
      }, {})
    },
    applyGroupAnswerState(questions, answers) {
      questions.forEach((question) => {
        const answer = answers[String(question.id)]
        const result = answer ? String(answer.result || '') : ''
        this.$set(question, 'result', result)
        this.$set(question, 'isRight', answer ? answer.isRight : null)
        if (Array.isArray(question.selectList)) {
          question.selectList.forEach((option) => {
            this.$set(option, 'selected', parseAnswer(result).includes(String(option.type)))
          })
        }
      })
    },
    persistGroupAnswer(question, result) {
      if (!this.isSkillCenterGroupPage) return
      const record = this.getGroupRecord()
      if (!record) return
      record.answers = record.answers && typeof record.answers === 'object'
        ? { ...record.answers }
        : {}
      record.answers[String(question.id)] = {
        result,
        isRight: question.isRight
      }
      record.answerNum = Object.values(record.answers).filter((answer) => {
        return answer && String(answer.result || '')
      }).length
      record.updateTime = this.formatGroupTime(new Date())
      this.saveGroupRecord(record)
    },
    clearGroupAnswers() {
      if (!this.isSkillCenterGroupPage) return false
      const record = this.getGroupRecord()
      if (record) {
        record.answers = {}
        record.answerNum = 0
        record.updateTime = this.formatGroupTime(new Date())
        this.saveGroupRecord(record)
      }
      this.questions.forEach((question) => {
        this.$set(question, 'result', '')
        this.$set(question, 'isRight', null)
        if (Array.isArray(question.selectList)) {
          question.selectList.forEach((option) => this.$set(option, 'selected', false))
        }
      })
      this.records = this.questions.map((question) => ({
        questionId: question.id,
        result: '',
        isRight: null
      }))
      return true
    },
    formatGroupTime(date) {
      const pad = (value) => String(value).padStart(2, '0')
      return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    async loadSkillCenterResourcePage(resourceType) {
      const params = {
        organCategoryId: this.organCategoryId,
        pageNum: 1,
        pageSize: 1000
      }
      if (this.categoryId) params.categoryId = this.categoryId
      const res = resourceType === 'wrong'
        ? await getSkillCenterWrongQuestionPage(params)
        : await getSkillCenterResourceQuestionPage({ ...params, resourceType })
      const data = res && res.data ? res.data : {}
      const rows = Array.isArray(data.rows) ? data.rows : []
      this.questions = normalizeQuestions(rows, rows)
    },
    loadPreviewQuestions() {
      const rows = Array.from({ length: 32 }, (_, index) => ({
        id: String(index + 1),
        content: '下列选项中，心电图结果一定无理的是（）？',
        type: index === 6 ? 2 : 0,
        correct: 'A',
        analysis: '胸骨角又称 Louis 角。位于胸骨上切迹约处，由胸骨柄与胸骨体的连接处向前突起而成。',
        selectList: [
          { type: 'A', name: '有理数与有理数的差' },
          { type: 'B', name: '有理数与有理数的差' },
          { type: 'C', name: '有理数与有理数的差' },
          { type: 'D', name: '有理数与有理数的差' }
        ],
        result: index < 3 ? 'C' : index === 3 ? 'A' : '',
        isFavorite: index === 3 ? 1 : 0
      }))
      this.questions = normalizeQuestions(rows, rows)
    },
    async loadPaperQuestions() {
      const studyPaperId = this.$route.query.studyPaperId
      const answerRes = await combinationAnswerSheet(studyPaperId)
      const records = answerRes && Array.isArray(answerRes.data) ? answerRes.data : []
      const res = await studyPaperGetById(studyPaperId)
      const questions = (res && res.data && res.data.questions) || []
      this.questions = normalizeQuestions(questions, records)
    },
    buildTypeFilters() {
      const values = new Set(
        this.questions
          .map((question) => question && question.type)
          .filter((type) => type !== undefined && type !== null && type !== '')
          .map(String)
      )
      this.typeFilters = [{ value: 'all', label: '全部' }].concat(
        QUESTION_TYPES
          .filter((item) => values.has(String(item.value)))
          .map((item) => ({ value: String(item.value), label: item.shortLabel }))
      )
      if (!this.typeFilters.some((item) => item.value === this.cardType)) {
        this.cardType = 'all'
      }
    },
    applyPreviewState() {
      if (String(this.$route.query.preview) !== '1') return
      const view = this.$route.query.view
      if (view === 'audio' && this.currentQuestion) {
        this.$set(this.currentQuestion, 'publicContent', '下列选项中，心电图结果一定无理的是（）？')
        this.$set(this.currentQuestion, 'publicFileUrl', 'preview-audio.mp3')
        this.$set(this.currentQuestion, 'content', '下列选项中，心电图结果一定无理的是（）？')
        this.$set(this.currentQuestion, 'result', 'C')
      }
      if (view === 'card' && this.hasAnswerCardFlow) this.answerCardVisible = true
      if (view === 'note') this.noteVisible = true
      if (view === 'correction') this.correctionVisible = true
      if (view === 'reset' && this.isPracticeSession) {
        this.answerCardVisible = true
        this.resetVisible = true
      }
      if (view === 'exit' && this.isPracticeSession) this.exitVisible = true
      if (view === 'stats' && this.isPracticeSession) {
        this.answerCardVisible = true
        this.statsVisible = true
      }
    },
    syncCurrentQuestion() {
      this.essayAnswer = this.currentQuestion && Number(this.currentQuestion.type) === 8
        ? this.currentQuestion.result || ''
        : ''
      this.noteContent = (this.currentQuestion && this.currentQuestion.mark) || ''
    },
    selectOption(option) {
      if (!this.currentQuestion || this.isAnswered || this.practiceMode === 'recite') return
      if (this.isMultiple) {
        this.$set(option, 'selected', !option.selected)
        this.currentQuestion.selectedList = this.currentQuestion.selectList
          .filter((item) => item.selected)
          .map((item) => item.type)
        if (this.practiceMode === 'fast') {
          const selected = this.currentQuestion.selectedList || []
          const correct = parseAnswer(this.currentQuestion.correct)
          if (selected.length >= correct.length) {
            this.submitFastAnswer(selected.join(','))
          }
        }
        return
      }
      this.currentQuestion.selectList.forEach((item) => this.$set(item, 'selected', item === option))
      this.$set(this.currentQuestion, 'pendingResult', option.type)
      if (this.practiceMode === 'fast') {
        this.submitFastAnswer(option.type)
      } else if (this.practiceMode === 'test') {
        this.scheduleTestModeAdvance(this.currentQuestion, option.type)
      }
    },
    async submitFastAnswer(result) {
      if (!result || !this.currentQuestion || this.isAnswered) return
      const question = this.currentQuestion
      const saved = await this.saveAnswer(result)
      if (!saved || this.currentQuestion !== question || this.practiceMode !== 'fast') return
      if (isAnswerRight(question, result)) {
        this.nextQuestion()
      }
    },
    scheduleTestModeAdvance(question, result) {
      this.clearTestModeAdvance()
      this.testModeAdvancing = true
      this.$nextTick(() => {
        this.testModeAdvanceTimer = setTimeout(async() => {
          this.testModeAdvanceTimer = null
          try {
            if (this.currentQuestion !== question || this.practiceMode !== 'test' || this.answerCardVisible) return
            const saved = await this.saveAnswer(result)
            if (!saved || this.currentQuestion !== question || this.practiceMode !== 'test' || this.answerCardVisible) return
            this.testModeAdvancing = false
            this.nextQuestion()
          } finally {
            this.testModeAdvancing = false
          }
        }, 350)
      })
    },
    clearTestModeAdvance() {
      if (this.testModeAdvanceTimer) {
        clearTimeout(this.testModeAdvanceTimer)
        this.testModeAdvanceTimer = null
      }
      this.testModeAdvancing = false
    },
    submitCurrentAnswer() {
      if (!this.currentQuestion || !this.canSubmitAnswer) return
      const type = Number(this.currentQuestion.type)
      if (type === 5) {
        this.submitMultipleAnswer()
      } else if (type === 7 || type === 8) {
        this.submitTextAnswer()
      } else {
        this.saveAnswer(this.currentQuestion.pendingResult)
      }
    },
    submitMultipleAnswer() {
      this.saveAnswer((this.currentQuestion.selectedList || []).join(','))
    },
    submitTextAnswer() {
      const isFill = Number(this.currentQuestion.type) === 7
      const blanks = this.currentQuestion.blanksList || []
      if (isFill && (!blanks.length || blanks.some((item) => !String(item.name || '').trim()))) {
        Toast('请填写完整答案')
        return
      }
      const result = isFill ? blanks.map((item) => item.name).join('[$]') : this.essayAnswer
      if (!String(result).trim()) {
        Toast('请先填写答案')
        return
      }
      this.saveAnswer(result)
    },
    async saveAnswer(result) {
      if (!this.currentQuestion || this.practiceMode === 'recite') return false
      const question = this.currentQuestion
      const categoryId = String(question.categoryId || this.categoryId || '')
      const params = {
        questionId: question.id,
        result
      }
      if (String(this.$route.query.skillCenter) !== '1') params.majorType = this.majorType
      if (categoryId) params.categoryId = categoryId
      if (this.$route.query.studyBankId) params.studyBankId = this.$route.query.studyBankId
      let res
      if (this.isHomeworkMode) {
        res = await saveSkillHomeworkAnswer({
          homeworkId: String(this.$route.query.homeworkId),
          homeworkContentId: String(this.$route.query.homeworkContentId),
          questionId: question.id,
          questionType: question.type,
          difficulty: question.difficult,
          result,
          isCorrect: isAnswerRight(question, result) ? 1 : 0
        })
      } else if (this.$route.query.studyPaperId) {
        res = await addQuestionResult({
          questionId: question.id,
          studyPaperId: this.$route.query.studyPaperId,
          result,
          majorType: this.majorType
        })
      } else {
        res = await addQuestion(params)
      }
      if (res && Number(res.code) === 200) {
        if (this.isHomeworkMode && res.data !== true) {
          Toast((res && (res.msg || res.message)) || '作答保存失败，请重试')
          return false
        }
        this.$set(question, 'result', result)
        this.$set(question, 'pendingResult', '')
        this.$set(question, 'isRight', isAnswerRight(question, result))
        const record = this.records[this.currentIndex]
        if (record) {
          record.result = result
          record.isRight = question.isRight
        }
        this.persistGroupAnswer(question, result)
        return true
      }
      return false
    },
    normalizeAnswer(value) {
      return String(value || '').split('').sort().join('')
    },
    optionClass(option) {
      const selected = this.isMultiple
        ? Boolean(option.selected)
        : parseAnswer(this.currentQuestion.result).includes(option.type) || this.currentQuestion.pendingResult === option.type
      if (!this.showAnswer) return selected ? 'selected' : ''
      const correct = parseAnswer(this.currentQuestion.correct)
      const result = parseAnswer(this.currentQuestion.result)
      if (correct.includes(option.type)) return 'correct'
      if (result.includes(option.type)) return 'wrong'
      return ''
    },
    isWrongOption(option) {
      const correct = parseAnswer(this.currentQuestion.correct)
      const result = parseAnswer(this.currentQuestion.result)
      return result.includes(option.type) && !correct.includes(option.type)
    },
    answerNumberClass(question) {
      if (!question.result) return ''
      return isAnswerRight(question, question.result) ? 'correct' : 'wrong'
    },
    requestStatistics() {
      if (!this.isPracticeSession) return
      if (this.unansweredCount > 0) {
        this.exitVisible = true
        return
      }
      this.showStatistics()
    },
    showStatistics() {
      this.exitVisible = false
      this.answerCardVisible = true
      this.statsVisible = true
    },
    closeStatistics() {
      this.statsVisible = false
    },
    closeExitDialog() {
      this.exitVisible = false
    },
    confirmExit() {
      this.showStatistics()
    },
    handleAnswerCardBack() {
      this.leavePage()
    },
    applyFocusIndexState() {
      const focusIndex = Number(this.$route.query.focusIndex)
      if (!this.$route.query.focusIndex || Number.isNaN(focusIndex)) return
      if (focusIndex < 0 || focusIndex >= this.questions.length) return
      this.currentIndex = focusIndex
      this.answerCardVisible = false
      window.scrollTo(0, 0)
    },
    jumpToQuestion(index) {
      this.currentIndex = index
      this.answerCardVisible = false
      window.scrollTo(0, 0)
    },
    previousQuestion() {
      const position = this.currentTypeQuestionPosition
      if (position > 0) {
        this.currentIndex = this.typeQuestionSequence[position - 1].index
      }
      window.scrollTo(0, 0)
    },
    nextQuestion() {
      const sequence = this.typeQuestionSequence
      const position = sequence.findIndex(({ index }) => index === this.currentIndex)
      if (position >= 0 && position < sequence.length - 1) {
        this.currentIndex = sequence[position + 1].index
        window.scrollTo(0, 0)
        return
      }
      if (this.cardType !== 'all') {
        Toast(`已经是${this.selectedQuestionTypeLabel || '当前题型'}的最后一题了`)
      } else if (this.isPracticeSession) {
        this.requestStatistics()
      }
    },
    handleQuestionTouchStart(event) {
      this.resetQuestionTouch()
      if (
        !this.currentQuestion ||
        this.answerCardVisible ||
        this.noteVisible ||
        this.correctionVisible ||
        this.resetVisible ||
        this.statsVisible
      ) return
      const touch = event && event.touches && event.touches[0]
      if (!touch) return
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.touchStartTarget = event.target
    },
    handleQuestionTouchMove(event) {
      const touch = event && event.touches && event.touches[0]
      if (!touch || !this.touchStartTarget || this.isQuestionSwipeBlockedTarget(this.touchStartTarget)) return
      const diffX = this.touchStartX - touch.clientX
      const diffY = this.touchStartY - touch.clientY
      if (Math.abs(diffX) > 12 && Math.abs(diffX) > Math.abs(diffY) && event.cancelable) {
        event.preventDefault()
      }
    },
    handleQuestionTouchEnd(event) {
      const touch = event && event.changedTouches && event.changedTouches[0]
      if (
        !touch ||
        !this.touchStartTarget ||
        this.answerCardVisible ||
        this.noteVisible ||
        this.correctionVisible ||
        this.resetVisible ||
        this.statsVisible
      ) {
        this.resetQuestionTouch()
        return
      }
      const diffX = this.touchStartX - touch.clientX
      const diffY = this.touchStartY - touch.clientY
      const blocked = this.isQuestionSwipeBlockedTarget(this.touchStartTarget || event.target)
      this.resetQuestionTouch()
      if (blocked || Math.abs(diffX) < 48 || Math.abs(diffX) <= Math.abs(diffY)) return
      if (event.cancelable) event.preventDefault()
      if (diffX > 0) this.nextQuestion()
      else this.previousQuestion()
    },
    isQuestionSwipeBlockedTarget(target) {
      // 文本输入框也要支持横向切题；仅保留会与横向手势冲突的控件。
      return Boolean(target && target.closest && target.closest('input[type="range"], select, a, audio, video'))
    },
    resetQuestionTouch() {
      this.touchStartX = 0
      this.touchStartY = 0
      this.touchStartTarget = null
    },
    confirmReset() {
      this.resetVisible = true
    },
    async resetAnswers() {
      if (this.clearGroupAnswers()) {
        this.resetVisible = false
        this.answerCardVisible = true
        this.cardCategory = 'all'
        this.currentIndex = 0
        this.syncCurrentQuestion()
        return
      }
      try {
        let res
        if (this.isHomeworkMode) {
          res = await resetSkillHomeworkContent(String(this.$route.query.homeworkContentId))
        } else if (String(this.$route.query.skillCenter) === '1') {
          const questionIds = Array.from(new Set(
            this.questions
              .map((question) => String(question.id || question.questionId || ''))
              .filter(Boolean)
          ))
          if (!questionIds.length) {
            Toast('未获取到题目')
            return
          }
          res = await resetQuestionRecords({ questionIds })
        } else {
          const categoryId = String(
            (this.currentQuestion && this.currentQuestion.categoryId) || this.categoryId || ''
          )
          if (!categoryId) {
            Toast('未获取到题库目录')
            return
          }
          res = await studyRecordRenew({ categoryId, majorType: this.majorType })
        }
        if (!res || Number(res.code) !== 200) {
          Toast((res && (res.msg || res.message)) || '重置失败，请稍后重试')
          return
        }
        this.questions.forEach((question) => {
          this.$set(question, 'result', '')
          this.$set(question, 'isRight', null)
          if (Array.isArray(question.selectedList)) {
            this.$set(question, 'selectedList', [])
          }
          if (Array.isArray(question.selectList)) {
            question.selectList.forEach((option) => this.$set(option, 'selected', false))
          }
          if (Array.isArray(question.blanksList)) {
            question.blanksList.forEach((blank) => this.$set(blank, 'name', ''))
          }
        })
        this.records = this.questions.map((question) => ({
          questionId: question.id,
          result: '',
          isRight: null
        }))
        this.currentIndex = 0
        this.cardCategory = 'all'
        this.resetVisible = false
        this.answerCardVisible = true
        this.syncCurrentQuestion()
      } catch (error) {
        if (isAuthError(error)) {
          this.handleAuthFailure()
          return
        }
        Toast((error && error.msg) || '重置失败，请稍后重试')
      }
    },
    openNote() {
      const mark = this.currentQuestion.mark || ''
      const imageMatches = mark.match(/\[图片\][^\s]+/g) || []
      this.noteImages = imageMatches.map((item) => item.replace('[图片]', '')).slice(0, 1)
      this.noteContent = mark.replace(/\n?\[图片\][^\s]+/g, '').trim()
      this.noteKeyboardOffset = 0
      this.noteKeyboardFocused = false
      this.noteVisible = true
    },
    bindNoteKeyboardListeners() {
      this.noteKeyboardViewportHandler = () => this.scheduleNoteKeyboardSync()
      this.noteKeyboardWindowHandler = () => this.scheduleNoteKeyboardSync()
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.noteKeyboardViewportHandler)
        window.visualViewport.addEventListener('scroll', this.noteKeyboardViewportHandler)
      }
      window.addEventListener('resize', this.noteKeyboardWindowHandler)
    },
    unbindNoteKeyboardListeners() {
      if (window.visualViewport && this.noteKeyboardViewportHandler) {
        window.visualViewport.removeEventListener('resize', this.noteKeyboardViewportHandler)
        window.visualViewport.removeEventListener('scroll', this.noteKeyboardViewportHandler)
      }
      if (this.noteKeyboardWindowHandler) {
        window.removeEventListener('resize', this.noteKeyboardWindowHandler)
      }
      this.noteKeyboardViewportHandler = null
      this.noteKeyboardWindowHandler = null
    },
    clearNoteKeyboardTimers() {
      this.noteKeyboardTimers.forEach((timer) => clearTimeout(timer))
      this.noteKeyboardTimers = []
    },
    scheduleNoteKeyboardSync() {
      this.clearNoteKeyboardTimers()
      if (!this.noteVisible && !this.correctionVisible) return
      // Android 和 iOS 键盘的可视区域变化有动画，分阶段测量保证弹层最终落在键盘上方。
      const delays = [0, 80, 200, 400]
      delays.forEach((delay) => {
        const timer = setTimeout(() => this.syncNoteKeyboardPosition(), delay)
        this.noteKeyboardTimers.push(timer)
      })
    },
    handleNoteFocus() {
      this.noteKeyboardFocused = true
      this.scheduleNoteKeyboardSync()
    },
    handleNoteBlur() {
      this.noteKeyboardFocused = false
      this.scheduleNoteKeyboardSync()
    },
    handleCorrectionFocus() {
      this.correctionKeyboardFocused = true
      this.scheduleNoteKeyboardSync()
    },
    handleCorrectionBlur() {
      this.correctionKeyboardFocused = false
      this.scheduleNoteKeyboardSync()
    },
    getKeyboardViewportHeight() {
      const viewport = window.visualViewport
      return Math.max(
        window.innerHeight || 0,
        document.documentElement && document.documentElement.clientHeight || 0,
        viewport && viewport.height || 0
      )
    },
    resetKeyboardSheetPositionIfClosed() {
      if (this.noteVisible || this.correctionVisible) {
        this.scheduleNoteKeyboardSync()
        return
      }
      this.clearNoteKeyboardTimers()
      this.noteKeyboardOffset = 0
      this.keyboardViewportHeight = 0
    },
    syncNoteKeyboardPosition() {
      if (!this.noteVisible && !this.correctionVisible) {
        this.noteKeyboardOffset = 0
        return
      }
      const viewport = window.visualViewport
      const currentHeight = viewport
        ? viewport.height
        : Math.max(window.innerHeight || 0, document.documentElement && document.documentElement.clientHeight || 0)
      const baselineHeight = Math.max(this.keyboardViewportHeight || 0, this.getKeyboardViewportHeight())
      const viewportInset = viewport
        ? Math.max(0, window.innerHeight - viewport.offsetTop - viewport.height)
        : 0
      const resizeInset = Math.max(0, baselineHeight - currentHeight)
      const keyboardInset = Math.max(viewportInset, resizeInset)
      const isFocused = this.noteKeyboardFocused || this.correctionKeyboardFocused
      const offset = keyboardInset > 80 && (isFocused || this.noteKeyboardOffset)
        ? keyboardInset + 8
        : 0
      if (offset !== this.noteKeyboardOffset) this.noteKeyboardOffset = offset
    },
    async saveNote() {
      const categoryId = String(this.currentQuestion.categoryId || this.categoryId || '')
      const mark = [
        this.noteContent.trim(),
        ...this.noteImages.map((url) => `[图片]${url}`)
      ].filter(Boolean).join('\n')
      const params = {
        categoryId,
        questionId: this.currentQuestion.id,
        mark,
        type: 1
      }
      if (String(this.$route.query.skillCenter) !== '1') params.majorType = this.majorType
      const res = await addQuestionResource(params)
      if (res && res.code === 200) {
        this.$set(this.currentQuestion, 'mark', mark)
        this.noteVisible = false
        Toast('保存成功')
      }
    },
    async toggleFavorite() {
      const favorite = Number(this.currentQuestion.isFavorite) === 1 ? 0 : 1
      const categoryId = String(this.currentQuestion.categoryId || this.categoryId || '')
      const params = {
        categoryId,
        questionId: this.currentQuestion.id,
        isFavorite: favorite,
        type: 0
      }
      if (String(this.$route.query.skillCenter) !== '1') params.majorType = this.majorType
      const res = await addQuestionResource(params)
      if (res && res.code === 200) {
        this.$set(this.currentQuestion, 'isFavorite', favorite)
        Toast(favorite ? '已收藏' : '取消收藏')
      }
    },
    openCorrection() {
      this.correctionType = '0'
      this.correctionContent = ''
      this.correctionImages = []
      this.correctionVisible = true
    },
    async uploadFile(file) {
      const form = new FormData()
      form.append('file', file.file)
      const res = await uploadImg(form)
      return res.data.url
    },
    async uploadNoteImage(file) {
      this.noteImages = [await this.uploadFile(file)]
    },
    async uploadCorrectionImage(file) {
      if (this.correctionImages.length >= 3) {
        Toast('最多可上传三张图片')
        return
      }
      this.correctionImages.push(await this.uploadFile(file))
    },
    openPhotoPicker(target) {
      if (target === 'note' && this.noteImages.length >= 1) {
        Toast('最多可上传一张图片')
        return
      }
      if (target === 'correction' && this.correctionImages.length >= 3) {
        Toast('最多可上传三张图片')
        return
      }
      this.$bridge.registermethod('getLocalPhoto', (data) => {
        if (!data) return
        if (target === 'note') {
          this.noteImages = [data]
        } else if (this.correctionImages.length < 3) {
          this.correctionImages.push(data)
        }
      })
      this.$bridge.callmethod('WebAction.openGetPhoto', {}, () => {})
    },
    async submitCorrection() {
      const res = await questionFeedBackErrorAdd({
        bank: this.currentQuestion.bank,
        questionId: this.currentQuestion.id,
        questionErrorType: this.correctionType,
        errorDescription: this.correctionContent,
        imageUrl: this.correctionImages.join(',')
      })
      if (res && res.code === 200) {
        this.correctionVisible = false
        Toast('提交成功')
      }
    },
    handleBack() {
      this.clearTestModeAdvance()
      if (!this.questions.length) {
        this.leavePage()
        return
      }
      this.answerCardVisible = true
      window.scrollTo(0, 0)
    },
    leavePage() {
      if (this.isHomeworkMode) {
        closeSkillCenterWeb(this, 'skillHomeworkQuestion')
        return
      }
      if (this.$route.query.from || this.source !== 'topic') {
        this.$router.back()
      } else {
        closeSkillCenterWeb(this, 'skillCenterQuestion')
      }
    },
    resolveMedia(path) {
      return mediaUrl(this, path)
    },
    mediaKind(path) {
      const normalized = String(path || '').split('?')[0].toLowerCase()
      if (/\.(png|jpe?g|gif|webp|bmp)$/.test(normalized)) return 'image'
      if (/\.(mp4|mov|m4v|webm)$/.test(normalized)) return 'video'
      if (/\.(mp3|wav|m4a|aac|ogg)$/.test(normalized)) return 'audio'
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.practice-page {
  width: 100%;
  min-height: 100vh;
  padding-bottom: 82px;
  overflow-x: hidden;
  touch-action: pan-y;
  background: #ffffff;
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  box-sizing: border-box;
}

button,
textarea,
input {
  font: inherit;
}

.history-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #f5f6f7;
  color: #4d5358;
  font-size: 21px;
}

.practice-content {
  padding: 10px 18px 24px;
}

.question-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
}

.question-meta > div:first-child {
  display: flex;
  gap: 6px;
}

.question-tags span {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 6px;
  border-radius: 6px;
  background: #f5f5f6;
  color: #9296a2;
  font-size: 12px;
}

.question-index {
  color: #9095a5;
  font-size: 14px;
}

.question-index b {
  margin-right: 6px;
  color: #111111;
  font-size: 17px;
  font-weight: 600;
}

.question-body {
  padding-top: 15px;
}

.question-public,
.question-title {
  color: #111111;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.75;
  word-break: break-word;
}

.question-title.essay-question-title {
  font-size: 15px;
  line-height: 22px;
}

.question-public {
  margin-bottom: 10px;
  padding: 12px;
  border-radius: 10px;
  background: #f7f8fa;
  color: #555b65;
  font-size: 15px;
  font-weight: 400;
}

.question-media {
  margin: 16px 20px 18px;
}

.question-media img,
.question-media video {
  display: block;
  width: 100%;
  max-height: 260px;
  border-radius: 10px;
  object-fit: contain;
}

.audio-player {
  padding: 22px 0 8px;
}

.audio-progress {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #9095a5;
  font-size: 12px;
}

.audio-progress input {
  width: 100%;
  accent-color: #1fc39c;
}

.audio-play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 28px auto 0;
  border: 0;
  border-radius: 50%;
  background: #1fc39c;
  color: #ffffff;
  font-size: 25px;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.option-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 49px;
  padding: 12px 14px;
  border: 0;
  border-radius: 11px;
  background: #f7f7f8;
  color: #111111;
  text-align: left;
}

.option-item.selected,
.option-item.correct {
  background: #e9faf6;
  color: #00ad94;
}

.option-item.wrong {
  background: #fff0f2;
  color: #ff3850;
}

.option-letter {
  width: 22px;
  flex: none;
  font-size: 15px;
}

.option-divider {
  width: 1px;
  height: 11px;
  margin-right: 14px;
  background: #d7d9dd;
}

.option-text {
  min-width: 0;
  font-size: 16px;
  line-height: 24px;
  word-break: break-word;
}

.option-wrong {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  margin-left: auto;
  border-radius: 50%;
  background: #ff3850;
  color: #ffffff;
  font-size: 14px;
}

.fill-list,
.essay-box {
  margin-top: 18px;
}

.fill-list input,
.essay-box textarea,
.bottom-sheet textarea {
  width: 100%;
  border: 1px solid #e8e9eb;
  border-radius: 13px;
  outline: none;
  background: #ffffff;
  box-sizing: border-box;
}

.fill-list input {
  height: 44px;
  margin-bottom: 10px;
  padding: 0 13px;
}

.essay-box textarea {
  min-height: 150px;
  padding: 13px;
  font-size: 14px;
  line-height: 24px;
  resize: none;
}

.submit-answer {
  display: block;
  width: 170px;
  height: 42px;
  margin: 18px auto 0;
  border: 0;
  border-radius: 22px;
  background: #2468ff;
  color: #ffffff;
  font-size: 16px;
}

.submit-answer:disabled {
  opacity: 0.45;
}

.answer-detail {
  margin-top: 25px;
  padding-top: 25px;
  border-top: 1px solid #eeeeee;
}

.answer-title,
.analysis-title {
  margin-bottom: 14px;
  font-size: 17px;
  font-weight: 600;
}

.analysis-title {
  margin-top: 25px;
}

.answer-line,
.analysis-content,
.essay-answer {
  color: #111111;
  font-size: 15px;
  line-height: 1.8;
}

.answer-correct {
  color: #00ad94;
}

.answer-user {
  color: #ff3850;
}

.answer-line button {
  float: right;
  height: 25px;
  padding: 0 13px;
  border: 1px solid #eeeeee;
  border-radius: 14px;
  background: #ffffff;
  color: #333333;
  font-size: 13px;
}

.practice-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  height: 78px;
  padding: 0 16px max(8px, env(safe-area-inset-bottom));
  border-top: 1px solid #eeeeee;
  background: rgba(255, 255, 255, 0.97);
  box-sizing: border-box;
}

.submit-footer-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 38px;
  margin-left: 5px;
  padding: 0;
  border: 0;
  border-radius: 19px;
  background: #2468ff;
  color: #ffffff;
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;
}

.submit-footer-button:disabled {
  opacity: 0.45;
}

.tool-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 43px;
  height: 50px;
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7e828b;
  font-size: 20px;
}

.tool-button + .tool-button {
  margin-left: 0;
}

.tool-button span {
  margin-top: 2px;
  font-size: 12px;
}

.tool-button.active {
  color: #ffad00;
}

.answer-card {
  position: fixed;
  inset: 0;
  z-index: 50;
  width: 100%;
  overflow-y: auto;
  padding-bottom: 52px;
  background: #ffffff;
}

.answer-filters {
  margin: 22px 20px 0;
  padding-bottom: 20px;
  border-bottom: 1px solid #eeeeee;
}

.filter-row {
  display: grid;
  grid-template-columns: 31px repeat(4, minmax(0, 1fr));
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.filter-row:last-child {
  margin-bottom: 0;
}

.filter-row > span {
  color: #8d929c;
  font-size: 13px;
}

.filter-row button {
  height: 27px;
  padding: 0 2px;
  overflow: hidden;
  border: 0;
  border-radius: 15px;
  background: #f5f6f7;
  color: #111111;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-row button.active {
  background: #2468ff;
  color: #ffffff;
}

.answer-grid {
  display: grid;
  grid-template-columns: repeat(6, 42px);
  justify-content: space-between;
  gap: 17px 0;
  padding: 24px 21px 90px;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.answer-grid::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.answer-grid button {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: #f4f5f7;
  color: #111111;
  font-size: 15px;
}

.answer-grid button.correct {
  background: #06c9a8;
  color: #ffffff;
}

.answer-grid button.wrong {
  background: #ff5848;
  color: #ffffff;
}

.answer-summary {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  min-height: 49px;
  padding: 14px 20px max(10px, env(safe-area-inset-bottom));
  background: #f4f5f7;
  color: #111111;
  font-size: 12px;
  text-align: center;
  box-sizing: border-box;
}

.answer-summary b {
  color: #00cfa5;
  font-weight: 400;
}

.bottom-sheet {
  padding: 14px 14px 10px;
  box-sizing: border-box;
}

.sheet-handle {
  width: 48px;
  height: 4px;
  margin: 0 auto 26px;
  border-radius: 3px;
  background: #171341;
}

.note-sheet textarea,
.correction-sheet textarea {
  height: 250px;
  padding: 14px;
  color: #111111;
  font-size: 15px;
  line-height: 1.7;
  resize: none;
}

.sheet-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 50px;
  gap: 13px;
}

.sheet-actions .van-icon {
  color: #4c5259;
  font-size: 26px;
}

.sheet-actions > button:last-child {
  width: 72px;
  height: 32px;
  border: 0;
  border-radius: 18px;
  background: #2468ff;
  color: #ffffff;
  font-size: 15px;
}

.sheet-actions > button:last-child:disabled {
  background: #e2e2e2;
  color: #aeb0b4;
}

.correction-types {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 18px;
}

.correction-types button {
  height: 33px;
  border: 0;
  border-radius: 9px;
  background: #f0f1f5;
  color: #111111;
  font-size: 14px;
}

.correction-types button.active {
  background: #2468ff;
  color: #ffffff;
  box-shadow: 0 5px 10px rgba(36, 104, 255, 0.22);
}

.uploaded-images {
  display: flex;
  margin-right: auto;
}

.uploaded-images span {
  position: relative;
  display: block;
  width: 36px;
  height: 36px;
  margin-right: 8px;
}

.uploaded-images img {
  width: 100%;
  height: 100%;
  border-radius: 3px;
  object-fit: cover;
}

.uploaded-images button {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 14px;
  height: 14px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #ff4d3f;
  color: #ffffff;
  line-height: 13px;
}

.photo-button {
  padding: 0;
  border: 0;
  background: transparent;
}

.dialog-message {
  padding: 18px 20px 24px;
  color: #565b69;
  font-size: 15px;
  line-height: 1.8;
  text-align: center;
}

.stats-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.stats-card {
  width: 300px;
  padding: 25px 20px 26px;
  border-radius: 17px;
  background: #ffffff;
  box-sizing: border-box;
}

.stats-card h3 {
  margin: 0 0 25px;
  font-size: 17px;
  font-weight: 600;
  text-align: center;
}

.stats-gauge {
  position: relative;
  width: 190px;
  height: 95px;
  margin: 0 auto 25px;
  overflow: hidden;
  border-radius: 190px 190px 0 0;
}

.stats-gauge::after {
  position: absolute;
  right: 9px;
  bottom: -1px;
  left: 9px;
  height: 86px;
  border-radius: 170px 170px 0 0;
  background: #ffffff;
  content: "";
}

.stats-gauge > div {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  text-align: center;
}

.stats-gauge b {
  font-size: 25px;
}

.stats-gauge span {
  color: #9095a5;
  font-size: 14px;
}

.stats-gauge small {
  display: block;
  color: #9095a5;
  font-size: 13px;
}

.stats-values {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.stats-values div {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stats-values b {
  font-size: 19px;
}

.stats-values span {
  margin-top: 4px;
  color: #9095a5;
  font-size: 12px;
}

.continue-button {
  width: 162px;
  height: 43px;
  margin-top: 23px;
  border: 0;
  border-radius: 22px;
  background: #2468ff;
  color: #ffffff;
  font-size: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 88px);
  color: #a2a6af;
  font-size: 14px;
}

.empty-state .van-icon {
  margin-bottom: 12px;
  font-size: 52px;
}

@media (min-width: 600px) {
  .practice-page,
  .answer-card,
  .practice-footer {
    right: auto;
    left: 50%;
    width: 375px;
    transform: translateX(-50%);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@import './index-visual.scss';
</style>
